<template>
  <div class="envio-list-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <!-- Hero -->
        <v-flex xs12>
          <div class="list-hero">
            <h1 class="list-title">
              {{ isManager ? sectionTitle : `Mis servicios · ${sectionTitle}` }}
            </h1>
            <p class="list-subtitle">
              Consulte los servicios, su bitácora y el progreso de cada gestión.
            </p>
          </div>
        </v-flex>

        <!-- Card principal -->
        <v-flex xs12>
          <v-card class="envio-card">
            <!-- Toolbar -->
            <v-card-text class="envio-card__toolbar">
              <div class="toolbar-row">
                <div class="toolbar-filters">
                  <div class="filter-group">
                    <label class="filter-label" for="logistics-template"
                      >Bitácora</label
                    >
                    <select
                      id="logistics-template"
                      v-model="filters.bitacora_template_id"
                      class="filter-input"
                      @change="onFilter"
                    >
                      <option value="">Todas las gestiones</option>
                      <option
                        v-for="t in templates"
                        :key="t.id"
                        :value="t.id"
                        >{{ t.name }}</option
                      >
                    </select>
                  </div>
                  <div class="filter-group">
                    <label class="filter-label">Desde</label>
                    <input
                      v-model="filters.date_from"
                      type="date"
                      class="filter-input"
                      @change="onFilter"
                    />
                  </div>
                  <div class="filter-group">
                    <label class="filter-label">Hasta</label>
                    <input
                      v-model="filters.date_to"
                      type="date"
                      class="filter-input"
                      @change="onFilter"
                    />
                  </div>
                  <div class="filter-group filter-group--search">
                    <label class="filter-label">Buscar</label>
                    <div class="filter-search-box">
                      <v-icon small class="filter-search-icon">search</v-icon>
                      <input
                        v-model="filters.term"
                        type="text"
                        class="filter-input filter-input--search"
                        placeholder="Factura, referencia, cliente..."
                        @keyup.enter="onFilter"
                        @input="onSearchInput"
                      />
                    </div>
                  </div>
                </div>
                <div v-if="isManager" class="toolbar-actions">
                  <v-btn
                    class="btn-new"
                    @click="
                      $router.push({
                        path: '/logistica/nuevo',
                        query: { tipo: activeType },
                      })
                    "
                  >
                    <v-icon left small>add</v-icon>
                    Nuevo servicio
                  </v-btn>
                </div>
              </div>
            </v-card-text>

            <!-- Tabs por tipo de asignación -->
            <div class="type-tabs" v-if="isManager">
              <button
                v-for="tab in typeTabs"
                :key="tab.value"
                type="button"
                class="type-tab"
                :class="{ 'type-tab--active': activeType === tab.value }"
                @click="onTypeChange(tab.value)"
              >
                <v-icon small class="type-tab__icon">{{ tab.icon }}</v-icon>
                {{ tab.label }}
              </button>
            </div>

            <div v-if="loadError" role="alert" class="pa-3">
              {{ loadError }} <v-btn flat @click="load">Reintentar</v-btn>
            </div>
            <!-- Loading bar -->
            <v-progress-linear
              v-if="pagination.loading"
              indeterminate
              color="#254F87"
              height="3"
              class="list-progress"
            ></v-progress-linear>

            <!-- Desktop table -->
            <v-card-text class="envio-card__content hidden-sm-and-down">
              <div class="table-wrap">
                <table class="envio-table" cellspacing="0">
                  <thead>
                    <tr>
                      <th>N° Factura / Tracking</th>
                      <th>Cliente</th>
                      <th>Origen</th>
                      <th>Destino</th>
                      <th>Bitácora</th>
                      <th>Asignado a</th>
                      <th>Estado</th>
                      <th class="th-actions">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="s in shipments" :key="s.id" class="envio-row">
                      <td>
                        <p class="cell-main">{{ s.invoiceNumber || '—' }}</p>
                        <p class="cell-sub">{{ s.trackingNumber }}</p>
                      </td>
                      <td>
                        <p class="cell-main">{{ s.clientName }}</p>
                        <p class="cell-sub">{{ s.clientPhone }}</p>
                      </td>
                      <td class="cell-location">{{ originOf(s) }}</td>
                      <td class="cell-location">{{ destOf(s) }}</td>
                      <td>
                        <span :class="typeClass(s.assignmentType)">
                          {{
                            s.bitacoraTemplateName ||
                              typeLabel(s.assignmentType)
                          }}
                        </span>
                      </td>
                      <td>
                        <span v-if="assigneeName(s)" class="assignee">
                          <v-icon x-small class="assignee__icon"
                            >account_circle</v-icon
                          >
                          {{ assigneeName(s) }}
                        </span>
                        <span v-else class="assignee-empty">Sin asignar</span>
                      </td>
                      <td>
                        <span :class="statusClass(s.currentStatus)">
                          {{ statusLabel(s.currentStatus) }}
                        </span>
                      </td>
                      <td>
                        <v-btn
                          icon
                          small
                          class="btn-detail"
                          :to="`/logistica/servicio/${s.id}`"
                          title="Ver detalle"
                        >
                          <v-icon small>visibility</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="!pagination.loading && shipments.length === 0">
                      <td colspan="8" class="empty-row">
                        <v-icon class="empty-row__icon">local_shipping</v-icon>
                        <p class="empty-row__text">
                          No se encontraron servicios con los filtros aplicados.
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>

            <!-- Mobile cards -->
            <v-card-text class="envio-card__content hidden-md-and-up">
              <div
                v-if="!pagination.loading && shipments.length === 0"
                class="mobile-empty"
              >
                No se encontraron servicios con los filtros aplicados.
              </div>
              <div
                v-for="s in shipments"
                :key="`m-${s.id}`"
                class="mobile-card"
              >
                <div class="mobile-card__head">
                  <div>
                    <p class="mobile-card__invoice">
                      {{ s.invoiceNumber || s.trackingNumber }}
                    </p>
                    <p class="mobile-card__tracking">{{ s.trackingNumber }}</p>
                  </div>
                  <span :class="statusClass(s.currentStatus)">{{
                    statusLabel(s.currentStatus)
                  }}</span>
                </div>
                <div class="mobile-card__body">
                  <div class="mobile-row">
                    <span class="mobile-label">Cliente</span>
                    <span class="mobile-value">{{ s.clientName }}</span>
                  </div>
                  <div class="mobile-row">
                    <span class="mobile-label">Origen</span>
                    <span class="mobile-value">{{ originOf(s) }}</span>
                  </div>
                  <div class="mobile-row">
                    <span class="mobile-label">Destino</span>
                    <span class="mobile-value">{{ destOf(s) }}</span>
                  </div>
                  <div class="mobile-row">
                    <span class="mobile-label">Bitácora</span>
                    <span class="mobile-value">
                      <span :class="typeClass(s.assignmentType)">{{
                        s.bitacoraTemplateName || typeLabel(s.assignmentType)
                      }}</span>
                    </span>
                  </div>
                  <div class="mobile-row">
                    <span class="mobile-label">Asignado</span>
                    <span class="mobile-value">{{
                      assigneeName(s) || 'Sin asignar'
                    }}</span>
                  </div>
                </div>
                <div class="mobile-card__foot">
                  <v-btn
                    flat
                    small
                    class="btn-detail-mobile"
                    :to="`/logistica/servicio/${s.id}`"
                  >
                    <v-icon left small>visibility</v-icon> Ver detalle
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Pagination -->
        <v-flex xs12 v-if="pagination.pages > 1">
          <div class="list-footer">
            <span class="list-footer__count">
              Mostrando {{ shipments.length }} de
              {{ pagination.totalItems }} servicios
            </span>
            <v-pagination
              v-model="pagination.page"
              :length="pagination.pages"
              :total-visible="7"
              color="#254F87"
              @input="load"
            ></v-pagination>
          </div>
        </v-flex>
        <v-flex
          xs12
          v-else-if="!pagination.loading && pagination.totalItems > 0"
        >
          <div class="list-footer list-footer--simple">
            {{ pagination.totalItems }} servicio{{
              pagination.totalItems !== 1 ? 's' : ''
            }}
            encontrado{{ pagination.totalItems !== 1 ? 's' : '' }}
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Api from '@/api'
import { logisticsManager } from '@/helpers/Logistics'
import SessionHelper from '@/helpers/SessionHelper'

function isoYearStart() {
  return `${new Date().getFullYear()}-01-01`
}
function isoMonthEnd() {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
    .toISOString()
    .slice(0, 10)
}

const STATUS_MAP = {
  pendiente: { label: 'Pendiente', cls: 'status-badge status-badge--pending' },
  asignado: { label: 'Asignado', cls: 'status-badge status-badge--assigned' },
  en_transito: {
    label: 'En tránsito',
    cls: 'status-badge status-badge--transit',
  },
  llegada: { label: 'Llegada', cls: 'status-badge status-badge--llegada' },
  evidencia: {
    label: 'Evidencia',
    cls: 'status-badge status-badge--evidencia',
  },
  firma: { label: 'Firma', cls: 'status-badge status-badge--firma' },
  entregado: {
    label: 'Entregado',
    cls: 'status-badge status-badge--delivered',
  },
}

export default {
  name: 'EnvioList',
  data() {
    return {
      filters: {
        date_from: logisticsManager() ? isoYearStart() : '',
        date_to: logisticsManager() ? isoMonthEnd() : '',
        term: '',
        bitacora_template_id: '',
      },
      activeType: this.$route.path.includes('transportista')
        ? 'transportista'
        : 'mensajero',
      templates: [],
      loadError: '',
      typeTabs: [
        {
          value: 'transportista',
          label: 'Transportista',
          icon: 'local_shipping',
        },
        { value: 'mensajero', label: 'Mensajería', icon: 'directions_bike' },
      ],
      searchTimer: null,
      pagination: {
        page: 1,
        rpp: 20,
        totalItems: 0,
        pages: 0,
        loading: true,
      },
    }
  },
  computed: {
    isManager: logisticsManager,
    sectionTitle() {
      return this.activeType === 'transportista'
        ? 'Transportista'
        : 'Mensajería'
    },
    ...mapState({ shipments: state => state.shipments.all }),
  },
  watch: {
    '$route.path'() {
      this.activeType = this.$route.path.includes('transportista')
        ? 'transportista'
        : 'mensajero'
      this.filters.bitacora_template_id = ''
      this.loadTemplates()
      this.onFilter()
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },
  created() {
    this.loadTemplates()
    if (this.$socket)
      this.$socket.emit(
        this.isManager ? 'join_admin_tracking' : 'join_transportista_room',
        { token: SessionHelper.token }
      )
    this.load()
  },
  methods: {
    async loadTemplates() {
      const rows = await Api.BitacoraTemplate.getAll({
        assignment_type: this.activeType,
        enabled: true,
      })
      this.templates = Array.isArray(rows) ? rows : []
    },
    async load() {
      this.pagination.loading = true
      this.loadError = ''
      const params = {
        rpp: this.pagination.rpp,
        page: this.pagination.page,
        date_from: this.filters.date_from,
        date_to: this.filters.date_to,
      }
      if (this.filters.bitacora_template_id)
        params.bitacora_template_id = this.filters.bitacora_template_id
      if (this.filters.term.trim()) params.term = this.filters.term.trim()
      if (this.activeType !== 'all') params.assignment_type = this.activeType
      try {
        const response = await this.$store.dispatch('shipments/getAll', params)
        if (!response || response.status >= 400)
          throw new Error('No se pudieron cargar los servicios. Reintente.')
        const total = (response && response.data && response.data.total) || 0
        this.pagination.totalItems = total
        this.pagination.pages = Math.ceil(total / this.pagination.rpp) || 0
      } catch (e) {
        this.loadError = e.message
        this.$store.commit('shipments/setShipments', [])
      } finally {
        this.pagination.loading = false
      }
    },
    onFilter() {
      this.pagination.page = 1
      this.load()
    },
    onTypeChange(value) {
      if (this.activeType === value) return
      this.$router.push(
        value === 'transportista'
          ? '/logistica/transportista'
          : '/logistica/mensajeria'
      )
    },
    onSearchInput() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.onFilter(), 450)
    },
    originOf(s) {
      if (!s.locations || !s.locations.length) return s.origin || '—'
      const loc = s.locations.find(l => l.type === 'origin')
      return loc ? loc.name || loc.address || '—' : '—'
    },
    destOf(s) {
      if (!s.locations || !s.locations.length) return s.destination || '—'
      const loc = s.locations.find(l => l.type === 'destination')
      return loc ? loc.name || loc.address || '—' : '—'
    },
    statusClass(status) {
      const s = (status || '').toLowerCase()
      return (STATUS_MAP[s] || { cls: 'status-badge status-badge--default' })
        .cls
    },
    statusLabel(status) {
      const s = (status || '').toLowerCase()
      return (STATUS_MAP[s] || { label: status || 'Desconocido' }).label
    },
    typeLabel(type) {
      return (type || '').toLowerCase() === 'mensajero'
        ? 'Mensajero'
        : 'Transportista'
    },
    assigneeName(s) {
      const u =
        (s && s.assignedUser) ||
        (s && s.assignee) ||
        (s && s.assignedTo) ||
        null
      if (!u) return ''
      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim()
      return name || u.name || u.email || ''
    },
    typeClass(type) {
      return (type || '').toLowerCase() === 'mensajero'
        ? 'type-chip type-chip--mensajero'
        : 'type-chip type-chip--transportista'
    },
  },

  // ── Socket.IO: actualizar estado en lista en tiempo real ──
  sockets: {
    connect() {
      if (this.$socket)
        this.$socket.emit(
          this.isManager ? 'join_admin_tracking' : 'join_transportista_room',
          { token: SessionHelper.token }
        )
    },
    'shipment:status_changed'(data) {
      if (!data || !data.shipmentId || !data.currentStatus) return
      const eventId = String(data.shipmentId)

      const shipments = this.$store.state.shipments.all
      const idx = shipments.findIndex(s => String(s.id) === eventId)
      if (idx !== -1) {
        const updated = { ...shipments[idx], currentStatus: data.currentStatus }
        const copy = [...shipments]
        copy.splice(idx, 1, updated)
        this.$store.commit('shipments/setShipments', copy)
        this.$toast.info(
          `Servicio ${data.trackingNumber || eventId}: ${this.statusLabel(
            data.currentStatus
          )}`
        )
      }
    },

    'shipment:assigned'(data) {
      if (!data || !data.shipmentId) return
      const eventId = String(data.shipmentId)
      const shipments = this.$store.state.shipments.all
      const exists = shipments.some(s => String(s.id) === eventId)
      if (!this.isManager) this.load()
      if (exists)
        this.$toast.info(
          `Mensajero asignado a servicio ${data.trackingNumber || eventId}.`
        )
    },
  },
}
</script>

<style scoped>
.envio-list-page {
  background: #f8fafc;
  min-height: 100vh;
  padding-bottom: 24px;
}

/* ── Hero ── */
.list-hero {
  padding: 4px 0 8px;
}
.list-crumb {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}
.list-title {
  margin: 4px 0;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
  color: #0f172a;
}
.list-subtitle {
  margin: 0;
  font-size: 14px;
  color: #475569;
}

/* ── Card ── */
.envio-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06) !important;
  background: #fff !important;
  overflow: hidden;
}

/* ── Toolbar ── */
.envio-card__toolbar {
  padding: 16px 20px !important;
  border-bottom: 1px solid #eef2f7;
  background: #fcfdff;
}

.toolbar-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}

.toolbar-filters {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex: 1;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-group--search {
  flex: 1;
  min-width: 200px;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.filter-input {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: #172133;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
  height: 38px;
}

.filter-input:focus {
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.08);
}

.filter-search-box {
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  padding: 0 10px;
  transition: border-color 0.15s;
  height: 38px;
}

.filter-search-box:focus-within {
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.08);
}

.filter-search-icon {
  color: #94a3b8 !important;
  flex-shrink: 0;
  margin-right: 6px;
}

.filter-input--search {
  border: none;
  box-shadow: none;
  padding: 0;
  height: auto;
  flex: 1;
}
.filter-input--search:focus {
  box-shadow: none;
}

.toolbar-actions {
  flex-shrink: 0;
}

.list-progress {
  margin: 0;
}

/* ── Tabs por tipo ── */
.type-tabs {
  display: flex;
  gap: 4px;
  padding: 8px 20px 0;
  border-bottom: 1px solid #eef2f7;
  background: #fff;
}

.type-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;
}

.type-tab:hover {
  color: #254f87;
}

.type-tab--active {
  color: #254f87;
  border-bottom-color: #254f87;
}

.type-tab__icon {
  color: inherit !important;
  font-size: 16px !important;
}

/* ── Chips de tipo ── */
.type-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}

.type-chip--transportista {
  background: #e8eef9;
  color: #2f4d7a;
}

.type-chip--mensajero {
  background: #ffedd5;
  color: #c2410c;
}

.assignee {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #334155;
  font-weight: 600;
}
.assignee__icon {
  color: #94a3b8 !important;
}
.assignee-empty {
  font-size: 12.5px;
  color: #cbd5e1;
  font-style: italic;
}

/* ── Table ── */
.envio-card__content {
  padding: 0 !important;
}

.table-wrap {
  overflow-x: auto;
}

.envio-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.envio-table th {
  padding: 11px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.th-actions {
  text-align: center;
}

.envio-row {
  transition: background 0.1s;
}
.envio-row:hover {
  background: #f8fafc;
}

.envio-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #475569;
  vertical-align: middle;
}

.envio-row:last-child td {
  border-bottom: 0;
}

.cell-main {
  margin: 0 0 2px;
  font-weight: 600;
  color: #172133;
  font-size: 13px;
}
.cell-sub {
  margin: 0;
  font-size: 11.5px;
  color: #94a3b8;
}

.cell-location {
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Status badges ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}

.status-badge--pending {
  background: #fef9c3;
  color: #854d0e;
}
.status-badge--assigned {
  background: #dbeafe;
  color: #1d4ed8;
}
.status-badge--transit {
  background: #ffedd5;
  color: #c2410c;
}
.status-badge--llegada {
  background: #f3e8ff;
  color: #7e22ce;
}
.status-badge--evidencia {
  background: #fef9c3;
  color: #854d0e;
}
.status-badge--firma {
  background: #e0f2fe;
  color: #0369a1;
}
.status-badge--delivered {
  background: #dcfce7;
  color: #15803d;
}
.status-badge--default {
  background: #f1f5f9;
  color: #475569;
}

/* ── Buttons ── */
.btn-new {
  background: #254f87 !important;
  color: #fff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: none !important;
  height: 38px !important;
}

.btn-detail {
  color: #254f87 !important;
  margin: 0 auto;
  display: block;
}

/* ── Empty row ── */
.empty-row {
  text-align: center;
  padding: 48px 20px !important;
}

.empty-row__icon {
  font-size: 40px !important;
  color: #cbd5e1 !important;
  display: block;
  margin-bottom: 8px;
}

.empty-row__text {
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
}

/* ── Mobile cards ── */
.envio-card__content {
  padding: 0 !important;
}

.mobile-empty {
  padding: 40px 20px;
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
}

.mobile-card {
  border-bottom: 1px solid #eef2f7;
  padding: 14px 16px;
}
.mobile-card:last-child {
  border-bottom: 0;
}

.mobile-card__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.mobile-card__invoice {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 700;
  color: #172133;
}
.mobile-card__tracking {
  margin: 0;
  font-size: 11.5px;
  color: #94a3b8;
}

.mobile-card__body {
  margin-bottom: 10px;
}

.mobile-row {
  display: flex;
  gap: 8px;
  padding: 4px 0;
  font-size: 12.5px;
  border-bottom: 1px solid #f8fafc;
}

.mobile-label {
  color: #94a3b8;
  font-weight: 600;
  min-width: 60px;
  flex-shrink: 0;
}
.mobile-value {
  color: #334155;
}

.mobile-card__foot {
  display: flex;
  justify-content: flex-end;
}

.btn-detail-mobile {
  color: #254f87 !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 12.5px !important;
}

/* ── Footer / pagination ── */
.list-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
}

.list-footer--simple {
  justify-content: flex-end;
  font-size: 13px;
  color: #94a3b8;
}

.list-footer__count {
  font-size: 13px;
  color: #64748b;
}

@media (max-width: 768px) {
  .toolbar-row {
    flex-direction: column;
    align-items: stretch;
  }
  .toolbar-actions {
    align-self: flex-end;
  }
}
</style>
